import { createTheme } from '@mui/material/styles';

/**
 * Palette values must be raw HSL so MUI can parse them for channel tokens.
 * Keep these in sync with the CSS custom properties in index.css.
 * Component styleOverrides use var() references directly.
 */
const palette = {
  primary:       'hsl(177, 100%, 36%)',
  primaryLight:  'hsl(177, 50%, 92%)',
  primaryDark:   'hsl(178, 47%, 32%)',
  primaryDarker: 'hsl(177, 100%, 20%)',
  bgCream:       'hsl(177, 30%, 95%)',
  textDark:      'hsl(177, 10%, 15%)',
  textMuted:     'hsl(177, 20%, 40%)',
  border:        'hsl(177, 30%, 85%)',
};

const v = {
  primary:         'var(--color-primary)',
  primaryHover:    'var(--color-primary-hover)',
  primaryRing:     'var(--color-primary-ring)',
  bgCream:         'var(--color-bg-cream)',
  textDark:        'var(--color-text-dark)',
  textPlaceholder: 'var(--color-text-placeholder)',
  border:          'var(--color-border)',
};

const theme = createTheme({
  cssVariables: true,
  palette: {
    primary: {
      main: palette.primary,
      light: palette.primaryLight,
      dark: palette.primaryDark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: palette.primaryDark,
      light: palette.primaryLight,
      dark: palette.primaryDarker,
      contrastText: '#ffffff',
    },
    background: {
      default: palette.bgCream,
      paper: '#ffffff',
    },
    text: {
      primary: palette.textDark,
      secondary: palette.textMuted,
    },
    divider: palette.border,
  },
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    caption: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '18px',
          lineHeight: '28px',
          padding: '12px 32px',
          borderRadius: '6px',
          gap: '8px',
        },
        containedPrimary: {
          backgroundColor: v.primary,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: v.primaryHover,
          },
          '&:focus': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${v.primaryRing}, 0 0 0 4px ${v.primary}`,
          },
        },
        outlinedPrimary: {
          backgroundColor: '#ffffff',
          color: v.primary,
          borderColor: v.primary,
          borderWidth: '2px',
          '&:hover': {
            backgroundColor: v.bgCream,
            borderWidth: '2px',
          },
        },
        sizeSmall: {
          fontSize: '0.875rem',
          height: 'var(--form-element-height)',
          padding: '0 1rem',
          borderRadius: '4px',
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            height: 'var(--form-element-height)',
            backgroundColor: '#ffffff',
            borderRadius: '4px',
            transition: 'all 200ms ease',
            '& fieldset': {
              borderColor: v.border,
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: v.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: v.primary,
              borderWidth: '2px',
              boxShadow: `0 0 0 2px ${v.primaryRing}`,
            },
          },
          '& .MuiOutlinedInput-input': {
            height: 'var(--form-element-height)',
            boxSizing: 'border-box',
            padding: '0 8px',
            color: v.textDark,
            '&::placeholder': {
              color: v.textPlaceholder,
              opacity: 1,
            },
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
              WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
              WebkitTextFillColor: v.textDark,
              caretColor: v.textDark,
            },
          },
          '& .MuiInputLabel-root': {
            color: v.textDark,
            fontWeight: 500,
            fontSize: '14px',
            '&.Mui-focused': {
              color: v.primary,
            },
          },
        },
      },
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: v.textDark,
          fontWeight: 500,
          fontSize: '14px',
          marginBottom: '8px',
          '&.Mui-focused': {
            color: v.primary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: v.textDark,
          fontWeight: 500,
          fontSize: '14px',
          '&.Mui-focused': {
            color: v.primary,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '16px',
          padding: '8px',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: v.primary,
          fontWeight: 700,
          fontSize: '24px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          border: `1px solid ${v.border}`,
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: v.primary,
        },
      },
    },
  },
});

export default theme;
